"use client";

import { X } from "lucide-react";
import { forwardRef } from "react";

import { useEstimator } from "../../_context/EstimatorContext";
import { useUndoableEdit } from "../../_hooks/useUndoableEdit";
import { CABIN_OPTIONS } from "../../_logic/constants";
import type { PlaceOption } from "../../_logic/places";
import type { Cabin } from "../../_logic/types";
import { groupName, originKey } from "../../_logic/workspace";
import { PopoverSelect } from "../ui/PopoverSelect";
import { CountStepper } from "./CountStepper";
import { PlaceCombobox, type PlaceComboboxBehavior, type PlaceComboboxHandle } from "./PlaceCombobox";

interface TravelerRowProps {
  /** The picked home city (null for a row still being filled in), its team and its priced airport. */
  place: { city: string | null; group?: string | null; airport?: string | null };
  count: number;
  /** Placeholder and screen-reader names for a row without a city yet. */
  labels?: { placeholder?: string; emptyLabel?: string; countLabel?: string };
  actions: { onPick: (place: PlaceOption) => void; onCount: (count: number) => void; onRemove: () => void };
  /** Handed to the city picker as is. */
  combobox?: PlaceComboboxBehavior;
}

/**
 * One home city in the Who editor: [city picker] [− count +] [remove], editable at any time, with
 * the group's airport and cabin under the city. The cabin chip stays quiet on the default and
 * turns indigo when the group flies in another class.
 * @param props - The row's city, team and count, and its handlers.
 * @returns The row.
 * @throws Never.
 */
export const TravelerRow = forwardRef<PlaceComboboxHandle, TravelerRowProps>(function TravelerRow(
  { place, count, labels = {}, actions, combobox },
  ref
) {
  // Hooks
  const { workspace } = useEstimator();
  const apply = useUndoableEdit();

  // Derived
  const { city, group, airport } = place;
  const { placeholder = "Search a city or airport", emptyLabel = "new home city", countLabel } = labels;
  const { onPick, onCount, onRemove } = actions;
  const name = city ?? emptyLabel;
  const origin = city
    ? workspace.origins.find((entry) => originKey(entry) === originKey({ city, group: group ?? undefined }))
    : null;
  const cabin = origin?.cabin ?? workspace.cabin;

  // Render
  return (
    <li className="flex items-start gap-2">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <PlaceCombobox
          ref={ref}
          value={city}
          placeholder={placeholder}
          ariaLabel={city ? `Home city: ${city}` : "Home city"}
          onPick={onPick}
          behavior={combobox}
        />
        {origin && (
          <span className="flex flex-wrap items-center gap-x-1.5 px-1 text-xs font-medium text-gray-600">
            {(group || airport) && (
              <span>{[group, airport ? `Flies from ${airport}` : null].filter(Boolean).join(" · ")}</span>
            )}
            {(group || airport) && <span aria-hidden>·</span>}
            <PopoverSelect
              ariaLabel={`Cabin for ${groupName(origin)}`}
              options={CABIN_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
              value={cabin}
              onChange={(next) =>
                apply(
                  {
                    type: "setOriginCabin",
                    key: originKey(origin),
                    cabin: next === workspace.cabin ? null : (next as Cabin),
                  },
                  `${groupName(origin)} in ${next}`
                )
              }
              trigger={{ compact: true, accent: Boolean(origin.cabin) && origin.cabin !== workspace.cabin }}
            />
          </span>
        )}
      </div>
      <CountStepper value={count} label={countLabel ?? `People from ${name}`} onChange={onCount} />
      {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
      <button
        type="button"
        aria-label={`Remove ${name}`}
        onClick={onRemove}
        className="flex size-10 shrink-0 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <X className="size-4" />
      </button>
    </li>
  );
});
